import { RenderManager } from '../../../core/RenderManager';

export class Terrain {
  private worldWidth: number;
  private worldHeight: number;
  private groundHeight: number;
  private terrainData: number[] = [];
  
  constructor(worldWidth: number, worldHeight: number) {
    this.worldWidth = worldWidth;
    this.worldHeight = worldHeight;
    this.groundHeight = worldHeight - 100; // Ground is 100 pixels from bottom
    
    this.generateTerrain();
  }

  private generateTerrain(): void {
    // Generate simple rolling hills terrain
    const resolution = 10; // Every 10 pixels
    const numPoints = Math.floor(this.worldWidth / resolution) + 1;
    
    for (let i = 0; i < numPoints; i++) {
      // Create gentle rolling hills with some randomness
      const baseHeight = this.groundHeight;
      const hillHeight = Math.sin(i * 0.02) * 30; // Gentle sine wave
      const randomVariation = (Math.random() - 0.5) * 10; // Small random variation
      
      this.terrainData[i] = baseHeight + hillHeight + randomVariation;
    }
    
    // Smooth the terrain to avoid sharp edges
    this.smoothTerrain();
  }

  private smoothTerrain(): void {
    const smoothedData = [...this.terrainData];
    
    for (let i = 1; i < this.terrainData.length - 1; i++) {
      smoothedData[i] = (this.terrainData[i - 1] + this.terrainData[i] + this.terrainData[i + 1]) / 3;
    }
    
    this.terrainData = smoothedData;
  }

  public getGroundHeight(x: number): number {
    // Clamp x to world bounds
    x = Math.max(0, Math.min(this.worldWidth, x));
    
    const resolution = 10;
    const index = Math.floor(x / resolution);
    
    if (index >= this.terrainData.length - 1) {
      return this.terrainData[this.terrainData.length - 1];
    }
    
    if (index < 0) {
      return this.terrainData[0];
    }
    
    // Linear interpolation between terrain points
    const x1 = index * resolution;
    const x2 = (index + 1) * resolution;
    const y1 = this.terrainData[index];
    const y2 = this.terrainData[index + 1];
    
    const t = (x - x1) / (x2 - x1);
    return y1 + (y2 - y1) * t;
  }

  public render(ctx: CanvasRenderingContext2D, cameraX: number): void {
    const screenWidth = 800; // Game screen width
    const startX = Math.floor(cameraX / 10) * 10; // Align to terrain resolution
    const endX = startX + screenWidth + 20; // Add some padding
    
    // Draw ground fill
    ctx.fillStyle = '#2D4A22'; // Dark green ground
    
    for (let x = startX; x < endX; x += 2) {
      if (x < 0 || x >= this.worldWidth) continue;
      
      const screenX = x - cameraX;
      if (screenX < -10 || screenX > screenWidth + 10) continue;
      
      const groundY = this.getGroundHeight(x);
      const fillHeight = this.worldHeight - groundY;
      
      ctx.fillRect(screenX, groundY, 2, fillHeight);
    }
    
    // Draw ground surface line
    ctx.strokeStyle = '#55A049'; // Brighter green surface
    ctx.lineWidth = 2;
    ctx.beginPath();
    
    let first = true;
    for (let x = startX; x < endX; x += 5) {
      if (x < 0 || x >= this.worldWidth) continue;
      
      const screenX = x - cameraX;
      if (screenX < -10 || screenX > screenWidth + 10) continue;
      
      const groundY = this.getGroundHeight(x);
      
      if (first) {
        ctx.moveTo(screenX, groundY);
        first = false;
      } else {
        ctx.lineTo(screenX, groundY);
      }
    }
    
    ctx.stroke();
    
    // Add some surface details (rocks, vegetation)
    this.renderSurfaceDetails(ctx, cameraX, startX, endX);
  }

  private renderSurfaceDetails(ctx: CanvasRenderingContext2D, cameraX: number, startX: number, endX: number): void {
    // Add small rocks and vegetation for visual interest
    for (let x = startX; x < endX; x += 50) { // Every 50 pixels
      if (x < 0 || x >= this.worldWidth) continue;
      
      const screenX = x - cameraX;
      if (screenX < -20 || screenX > 820) continue;
      
      const groundY = this.getGroundHeight(x);
      
      // Add small random details
      if (Math.floor(x / 50) % 3 === 0) {
        // Small rock
        ctx.fillStyle = '#787878';
        ctx.fillRect(screenX, groundY - 4, 4, 4);
      } else if (Math.floor(x / 50) % 3 === 1) {
        // Small plant
        ctx.fillStyle = '#BFCE72';
        ctx.fillRect(screenX, groundY - 6, 2, 6);
        ctx.fillRect(screenX - 1, groundY - 4, 4, 2);
      }
      // Every 3rd position has no detail for variation
    }
  }

  public isOnGround(x: number, y: number, height: number = 0): boolean {
    const groundY = this.getGroundHeight(x);
    return y + height >= groundY - 5; // 5 pixel tolerance
  }

  public getWorldWidth(): number {
    return this.worldWidth;
  }

  public getWorldHeight(): number {
    return this.worldHeight;
  }
}